import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, UserPlus } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/api/client'
import { describeApiError } from '@/lib/apiError'

/**
 * Manual customer add — the marketing edition's front door for walk-in
 * enrolment from the directory (previously a customers row could only be
 * born through an order, storefront signup, loyalty claim or the join page).
 *
 * Dedup lives server-side (resolveCustomerInTx's match ladder): on
 * created=false we say "matched an existing customer" and open their
 * profile instead of pretending a duplicate was created. Phone-shape
 * validation is also server-side (400 invalid_phone carries the operator
 * message) — the input only hints at the expected format.
 */
export function AddCustomerDialog({
  open,
  onOpenChange,
  onSaved,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called with the customer id after a successful save (created or matched). */
  onSaved?: (id: string, created: boolean) => void
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [birthday, setBirthday] = useState('')
  // Opt-out model (2026-09-04): consent is on by default; staff untick it
  // only when the guest asked not to be messaged.
  const [optIn, setOptIn] = useState(true)

  const reset = () => {
    setName('')
    setPhone('')
    setEmail('')
    setBirthday('')
    setOptIn(false)
  }

  const save = useMutation({
    mutationFn: () =>
      apiClient.createAdminCustomer({
        display_name: name.trim(),
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        birthday: birthday || undefined,
        whatsapp_opt_in: optIn,
      }),
    onSuccess: (res) => {
      const data = res.data
      if (!res.success || !data) {
        toast({
          title: 'Could not save customer',
          description: res.message || 'Unknown error',
          variant: 'destructive',
        })
        return
      }
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      toast({
        title: data.created ? 'Customer added' : 'Matched an existing customer',
        description: data.created
          ? `${name.trim()} is now in the directory.`
          : 'These details belong to a customer already in the directory — their profile was updated, not duplicated.',
      })
      onSaved?.(data.id, data.created)
      reset()
      onOpenChange(false)
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not save customer',
        description: describeApiError(err).message,
        variant: 'destructive',
      })
    },
  })

  const canSave =
    name.trim() !== '' && (phone.trim() !== '' || email.trim() !== '') && !save.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" aria-hidden /> Add customer
          </DialogTitle>
          <DialogDescription>
            Add a guest to the directory. If the phone or email already exists,
            their profile is updated instead of duplicated.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="add-cust-name">Name</Label>
            <Input
              id="add-cust-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ali Raza"
              autoComplete="off"
              maxLength={100}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="add-cust-phone">Phone</Label>
            <Input
              id="add-cust-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="0300 1234567"
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">
              Pakistani mobile numbers dedup best (0300…, +92300…).
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="add-cust-email">Email (optional)</Label>
            <Input
              id="add-cust-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ali@example.com"
              autoComplete="off"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="add-cust-birthday">Birthday (optional)</Label>
            <Input
              id="add-cust-birthday"
              type="date"
              value={birthday}
              onChange={(e) => setBirthday(e.target.value)}
            />
          </div>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              checked={optIn}
              onCheckedChange={(v) => setOptIn(v === true)}
              className="mt-0.5"
            />
            <span>
              Send WhatsApp offers and updates
              <span className="block text-xs text-muted-foreground">
                On by default. Untick if the guest asked not to be messaged —
                they can also reply STOP on WhatsApp at any time.
              </span>
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={!canSave}>
            {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
            Save customer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
